/**
 * SPEC-TrainingSimulator Phase B — client fetchers + hooks for the trainee
 * runner (§10, guard `T`). Every runner call lives here so the pages never
 * hand-roll a fetch and train-runner-structure.test.ts can pin the endpoint
 * list in one place. Response shapes mirror server/routes/simulator-runner.ts.
 *
 * Isolation (§8.5): nothing here touches /api/qa/*, claims or sockets.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import type {
  AnswerEntry,
  ClockReading,
  FeedbackResolution,
  LessonPlacement,
  PhaseCard,
  QuestionGrade,
  QuestionId,
  QuestionReveal,
  ReviewStatus,
  SimulatorQuestionConfig,
  TraineeSnapshot,
} from "@shared/simulator-helpers";
import type { BadReason } from "@shared/schema";

export interface RunnerHome {
  preview: boolean;
  trainee: { id: string; name: string };
  phases: PhaseCard[];
  orientation: { lessons: Array<{ id: string; title: string; read: boolean }> };
}

/**
 * The attempt as a TRAINEE receives it (`traineeAttemptOut` on the server).
 *
 * No `attemptNo`, no timestamps: D8 as amended forbids the trainee an attempt
 * number, and nothing on the runner ever read the rest. Declaring the Senior
 * fields here is what let them ride along unnoticed (Phase D audit H2) — keep
 * this shape identical to the server serialiser.
 */
export interface RunnerAttempt { id: string; phase: number; status: string }

export interface RunnerLesson { id: string; title: string; bodyMd: string; images: Array<{ src: string; alt?: string; caption?: string }>; placement: LessonPlacement }

export interface GradedQuestion {
  questionId: QuestionId;
  grade: QuestionGrade;
  feedback: FeedbackResolution;
  reveal: QuestionReveal;
  asked: QuestionId[] | null;
  stored?: boolean;
}

export type RunnerNext =
  | { kind: "lesson"; attempt: RunnerAttempt; lesson: RunnerLesson; index: number; total: number }
  | {
      kind: "item";
      preview: boolean;
      phase: number;
      attempt: RunnerAttempt | null;
      item: { id: string; index: number; total: number; snapshot: TraineeSnapshot };
      questions: SimulatorQuestionConfig[];
      answered: Record<string, { answer: AnswerEntry } & Omit<GradedQuestion, "asked">>;
      asked: QuestionId[] | null;
      /** §8.4 — null in Phase 1, where there is no clock. */
      clock: ClockReading | null;
    }
  | { kind: "done"; attempt: RunnerAttempt }
  /** Phase 2 only (D18): the phase is finished and the review screen is due. */
  | { kind: "review"; attempt: RunnerAttempt };

// ─── Phase 2 review (§8.3, D18) ──────────────────────────────────────────────

export interface ReviewQuestion {
  questionId: QuestionId;
  sort: number;
  prompt: string;
  status: ReviewStatus;
  answer: AnswerEntry | null;
  grade: QuestionGrade | null;
  feedback: FeedbackResolution | null;
  reveal: QuestionReveal | null;
}

export interface ReviewItem {
  itemId: string;
  index: number;
  recipientName: string | null;
  address: string | null;
  elapsedMs: number | null;
  /** Names the evidence a `where[]` answer points at — no photo URLs (§8.3). */
  evidence: { photoCount: number; hasCompletionNote: boolean; hasDestinationNotes: boolean };
  questions: ReviewQuestion[];
}

export interface RunnerReview {
  attempt: RunnerAttempt;
  questions: SimulatorQuestionConfig[];
  items: ReviewItem[];
  totals: { correct: number; graded: number; perItem: Array<{ correct: number; graded: number }> };
}

export const RUN_KEYS = {
  home: ["/api/simulator/home"] as const,
  next: (attemptId: string) => ["/api/simulator/attempts", attemptId, "next"] as const,
  lesson: (id: string) => ["/api/simulator/lessons", id] as const,
  preview: (itemId: string) => ["/api/simulator/preview/items", itemId] as const,
  review: (attemptId: string) => ["/api/simulator/attempts", attemptId, "review"] as const,
};

async function getJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) throw new Error(`${res.status}: ${(await res.text()) || res.statusText}`);
  return res.json() as Promise<T>;
}

export function useRunnerHome() {
  return useQuery<RunnerHome>({ queryKey: RUN_KEYS.home, queryFn: () => getJson("/api/simulator/home"), staleTime: 0 });
}

export function useStartAttempt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (phase: number) => {
      const res = await apiRequest("POST", "/api/simulator/attempts", { phase });
      return res.json() as Promise<{ attempt: RunnerAttempt; resumed: boolean }>;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: RUN_KEYS.home }); },
  });
}

export function useAttemptNext(attemptId: string | null | undefined) {
  return useQuery<RunnerNext>({
    queryKey: RUN_KEYS.next(attemptId ?? ""),
    queryFn: () => getJson(`/api/simulator/attempts/${attemptId}/next`),
    enabled: !!attemptId,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
}

export function useRunnerLesson(id: string | null | undefined) {
  return useQuery<{ lesson: RunnerLesson; read: boolean }>({
    queryKey: RUN_KEYS.lesson(id ?? ""),
    queryFn: () => getJson(`/api/simulator/lessons/${id}`),
    enabled: !!id,
  });
}

export function useMarkLessonRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("POST", `/api/simulator/lessons/${id}/read`);
      return res.json() as Promise<{ read: true; next: string | null }>;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: RUN_KEYS.home }); },
  });
}

export interface AnswerInput { attemptId: string; itemId: string; questionId: QuestionId; answer: AnswerEntry }

export function useAnswerQuestion() {
  return useMutation({
    mutationFn: async ({ attemptId, itemId, questionId, answer }: AnswerInput) => {
      const res = await apiRequest("POST", `/api/simulator/attempts/${attemptId}/items/${itemId}/answer`, { questionId, answer });
      return res.json() as Promise<GradedQuestion>;
    },
  });
}

/**
 * §10 — q10 in phases 1–2, and the REAL footer + resolution panel in 3–4.
 * The panel's Step 2 (initial action) has no column of its own: the run page
 * folds it into `resolutionNotes` with `foldResolutionNotes` before calling
 * this (Sarah's call, 2026-09-09).
 */
export interface DecideInput {
  attemptId: string;
  itemId: string;
  decision: "GOOD" | "BAD";
  badReason?: BadReason | null;
  badReasonOther?: string | null;
  resolutionNotes?: string | null;
}

export type AdvancePointer = { next: "item" | "done" | "review"; index: number; total: number };

export function useDecideItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ attemptId, itemId, ...body }: DecideInput) => {
      const res = await apiRequest("POST", `/api/simulator/attempts/${attemptId}/items/${itemId}/decision`, body);
      return res.json() as Promise<AdvancePointer>;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: RUN_KEYS.next(vars.attemptId) });
      qc.invalidateQueries({ queryKey: RUN_KEYS.home });
    },
  });
}

/** §8.4 — "+ Time" (Phase 3). Every press writes a `simulator_time_events` row server-side. */
export function useExtendTime() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ attemptId, itemId }: { attemptId: string; itemId: string }) => {
      const res = await apiRequest("POST", `/api/simulator/attempts/${attemptId}/items/${itemId}/extend`, {});
      return res.json() as Promise<{ extended: true; seconds: number; clock: ClockReading }>;
    },
    onSuccess: (_d, vars) => { qc.invalidateQueries({ queryKey: RUN_KEYS.next(vars.attemptId) }); },
  });
}

/** §8.4 — the hard clock hit 0:00 (Phase 4). The SERVER decides whether it really did. */
export function useTimeoutItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ attemptId, itemId }: { attemptId: string; itemId: string }) => {
      const res = await apiRequest("POST", `/api/simulator/attempts/${attemptId}/items/${itemId}/timeout`, {});
      return res.json() as Promise<AdvancePointer>;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: RUN_KEYS.next(vars.attemptId) });
      qc.invalidateQueries({ queryKey: RUN_KEYS.home });
    },
  });
}

export function useAttemptReview(attemptId: string | null | undefined) {
  return useQuery<RunnerReview>({
    queryKey: RUN_KEYS.review(attemptId ?? ""),
    queryFn: () => getJson(`/api/simulator/attempts/${attemptId}/review`),
    enabled: !!attemptId,
    refetchOnWindowFocus: false,
  });
}

// ─── Senior/Admin preview (§3) — nothing recorded ────────────────────────────

/**
 * Preview one item as a trainee would meet it (§3, §12 Q5).
 *
 * `phase` forwards the page's `?phase=` so a Senior can see an item's OTHER
 * phase screen — an item eligible for {1,3} previews as Phase 1 by default, and
 * this is the only way to reach its Phase 3 layout. The server defaults to the
 * item's own eligibility when it is omitted. It is part of the query key, or the
 * two phases would share one cache entry (audit M4).
 */
export function usePreviewItem(itemId: string | null | undefined, phase?: number | null) {
  return useQuery<RunnerNext>({
    queryKey: [...RUN_KEYS.preview(itemId ?? ""), phase ?? null],
    queryFn: () => getJson(`/api/simulator/preview/items/${itemId}${phase ? `?phase=${phase}` : ""}`),
    enabled: !!itemId,
    refetchOnWindowFocus: false,
  });
}

export function usePreviewAnswer() {
  return useMutation({
    mutationFn: async ({ itemId, questionId, answer }: { itemId: string; questionId: QuestionId; answer: AnswerEntry }) => {
      const res = await apiRequest("POST", `/api/simulator/preview/items/${itemId}/answer`, { questionId, answer });
      return res.json() as Promise<GradedQuestion>;
    },
  });
}
